import { AiMessageType, DisplayItem } from '#/types/message.ts';
import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './ai-message.module.css'
import warningIcon from '#/assets/warning.svg'
import { FeedbackOption, FeedbackType } from '#/types/feedback.ts';
import { FeedbackButtons } from './feedback/desktop-feedback/FeedbackButtons.tsx';
import { MobileFeedbackPopup } from './feedback/mobile-feedback/MobileFeedbackPopup.tsx';
import { FeedbackDialog } from './feedback/FeedbackDialog.tsx';
import { AlertPopup, Backdrop } from './alert-popup/AlertPopup.tsx';
import { MetadataBubble } from './metadata-bubble/MetadataBubble.tsx';
import { FeatureSelection } from '../../feature-selection/feature-selection.tsx';
import { MarkdownRenderer } from '../markdown-renderer/MarkdownRenderer.tsx';
import { ChecklistPanel } from '../checklist-panel/checklist-panel.tsx';
import { useWebSocket, WebSocketCommand } from '#/contexts/WebsocketContext.tsx';
import { useChecklist, ChecklistState } from '#/contexts/ChecklistContext';
import { PlaybackVisualizer } from '../../audio-playback/playback-visualizer';
import { useAudioPlayback } from "#/contexts/PlaybackContext";


interface AiMessageProps {
  message: AiMessageType;
  openModal: (item: DisplayItem, index: number, allItemsFromCarousel: DisplayItem[], imageListId: string) => void;
  onFeatureSelect: (selections: { feature_name: string, option_name: string }[]) => void;
  isLatestMessage?: boolean;
  audioOutputFileName?: string;
  isMuted?: boolean;
  isFirstMessage?: boolean;
  isExhibitionMode?: boolean;
  overrideAudioUrl?: string;
  onPlaybackFinish?: () => void;
}

function AiMessage({ message, openModal, onFeatureSelect, isLatestMessage,
                     audioOutputFileName, isMuted, isFirstMessage,
                     isExhibitionMode, overrideAudioUrl, onPlaybackFinish }: AiMessageProps) {
  const { t } = useTranslation();
  const {
    setChecklistState,
    setChecklistId,
    renderChecklist,
    getChecklist,
  } = useChecklist();
  const { sendMessage } = useWebSocket();
  const { isPlaying } = useAudioPlayback();

  const [showFeedback, setShowFeedback] = useState(false);
  const [isHiding, setIsHiding] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackType | null>(null);
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [otherReason, setOtherReason] = useState('');
  const [isShowingFeedback, setIsShowingFeedback] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [showMobileFeedback, setShowMobileFeedback] = useState(false);
  const [touchPosition, setTouchPosition] = useState({ x: 0, y: 0 });
  const [pressedButton, setPressedButton] = useState<FeedbackType | null>(null);
  const [deselecting, setDeselecting] = useState<FeedbackType | null>(null);
  const [enableClickOutside, setEnableClickOutside] = useState(false);
  const [isAlertPopupOpen, setIsAlertPopupOpen] = useState(false);

  const [touchStartPosition, setTouchStartPosition] = useState({ x: 0, y: 0 });
  const [isLongPressing, setIsLongPressing] = useState(false);
  const [showFeatureSelection, setShowFeatureSelection] = useState(false);

  const longPressTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const visualFeedbackTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const dialogRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const messageRef = useRef<HTMLDivElement>(null);
  const visualizerRef = useRef<HTMLDivElement>(null);
  const startScrollYRef = useRef(0);

  const pointerIdRef = useRef<number | null>(null);
  const isPressActiveRef = useRef(false);
  const longPressTriggeredRef = useRef(false);
  const PRESS_SLOP_PX = 12;

  useEffect(() => {
    if (showFeedback && containerRef.current && dialogRef.current) {
      requestAnimationFrame(() => {
        if (containerRef.current && dialogRef.current) {
          const height = dialogRef.current.offsetHeight;
          containerRef.current.style.height = `${height}px`;
          setIsShowingFeedback(true);
        }
      });

      setTimeout(() => {
        dialogRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }, 100);
    }
  }, [showFeedback, showOtherInput]);

  const handleHideFeedback = () => {
    if (containerRef.current && dialogRef.current) {
      setIsHiding(true);
      containerRef.current.style.height = `${dialogRef.current.offsetHeight}px`;
      containerRef.current.offsetHeight;
      containerRef.current.style.height = '0';

      const message = JSON.stringify({
        command: WebSocketCommand.PROMPT_FEEDBACK,
        payload: {
          type: "negative",
          selected_reasons: selectedReasons.length > 0 ? selectedReasons : null,
          free_text: otherReason || null
        }
      });
      sendMessage(message);
    }
    setTimeout(() => {
      setShowFeedback(false);
      setIsHiding(false);
    }, 500);
  };

  const sendNegativeFeedback = (reasons: string[] = [], freeText: string = '') => {
    const message = JSON.stringify({
      command: WebSocketCommand.PROMPT_FEEDBACK,
      payload: {
        type: "negative",
        selected_reasons: reasons.length > 0 ? reasons : null,
        free_text: freeText || null
      }
    });
    sendMessage(message);
  };

  const handleFeedback = (type: FeedbackType) => {
    if (type === selectedFeedback) {
      setSelectedFeedback(null);
      handleHideFeedback();
      setShowOtherInput(false);
      const message = JSON.stringify({
        command: WebSocketCommand.PROMPT_FEEDBACK
      });
      sendMessage(message);
      return;
    }

    setSelectedFeedback(type);
    if (type === FeedbackType.POSITIVE) {
      handleHideFeedback();
      setShowOtherInput(false);
      const message = JSON.stringify({
        command: WebSocketCommand.PROMPT_FEEDBACK,
        payload: {
          type: type
        }
      });
      sendMessage(message);
    } else {
      sendNegativeFeedback(selectedReasons, otherReason);
      setShowFeedback(true);
    }
  };

  const handleFeedbackReason = (reason: string) => {
    const newReasons = selectedReasons.includes(reason)
      ? selectedReasons.filter(r => r !== reason)
      : [...selectedReasons, reason];

    setSelectedReasons(newReasons);
    sendNegativeFeedback(newReasons, otherReason);

    if (reason === FeedbackOption.OTHER && !selectedReasons.includes(reason)) {
      setShowOtherInput(true);
    }
  };

  const handleShowOtherInput = (show: boolean) => {
    if (!show) {
      sendNegativeFeedback(selectedReasons, otherReason);
    }
    setShowOtherInput(show);
  };

  const handlePressStart = () => {
    if (messageRef.current) {
      const messageElement = messageRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      // Calculate the visible portion of the message
      const visibleTop = Math.max(messageElement.top, 0);
      const visibleBottom = Math.min(messageElement.bottom, viewportHeight);
      const visibleMiddle = visibleTop + (visibleBottom - visibleTop) / 2;

      // Ensure x position is within viewport
      const x = Math.min(
        Math.max(messageElement.left + messageElement.width / 2, 50),
        viewportWidth - 50
      );

      setTouchPosition({
        x: x,
        y: visibleMiddle
      });
      setShowMobileFeedback(true);

      setTimeout(() => {
        setEnableClickOutside(true);
      }, 300);
    }
  };

  const onAnyScroll = () => cancelLongPress();

  const removeScrollGuards = () => {
    window.removeEventListener('scroll', onAnyScroll, { capture: true } as any);
    document.removeEventListener('scroll', onAnyScroll, { capture: true } as any);
  };

  const handleMouseDown = () => {
    if (window.innerWidth < 639) {
      isPressActiveRef.current = true;
      longPressTimeoutRef.current = setTimeout(() => {
        if (!isPressActiveRef.current) return;
        handlePressStart();
      }, 700);
    }
  };

  const handleMouseUp = () => {
    cancelLongPress();
  };

  const cancelLongPress = () => {
    isPressActiveRef.current = false;
    longPressTriggeredRef.current = false;
    setIsLongPressing(false);
    if (longPressTimeoutRef.current) {
      clearTimeout(longPressTimeoutRef.current);
      longPressTimeoutRef.current = undefined;
    }
    if (visualFeedbackTimeoutRef.current) {
      clearTimeout(visualFeedbackTimeoutRef.current);
      visualFeedbackTimeoutRef.current = undefined;
    }
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    if (window.innerWidth > 639) return;         // mobile only
    const target = e.target as HTMLElement;
    if (target.closest('button, a, [role="button"]')) {
      // Don't start long-press on interactive elements
      return;
    }

    // capture so we keep getting move/up/cancel even if finger leaves element
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    pointerIdRef.current = e.pointerId;

    isPressActiveRef.current = true;
    longPressTriggeredRef.current = false;
    setTouchStartPosition({ x: e.clientX, y: e.clientY });
    startScrollYRef.current =
      window.scrollY || document.documentElement.scrollTop || 0;

    visualFeedbackTimeoutRef.current = setTimeout(() => {
      if (!isPressActiveRef.current) return;
      setIsLongPressing(true);
    }, 300);

    longPressTimeoutRef.current = setTimeout(() => {
      if (!isPressActiveRef.current) return;
      longPressTriggeredRef.current = true;
      handlePressStart();
    }, 700);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!longPressTimeoutRef.current) return;

    const dx = Math.abs(e.clientX - touchStartPosition.x);
    const dy = Math.abs(e.clientY - touchStartPosition.y);
    const scrollY =
      window.scrollY || document.documentElement.scrollTop || 0;

    if (dx > PRESS_SLOP_PX || dy > PRESS_SLOP_PX || Math.abs(scrollY - startScrollYRef.current) > 6) {
      cancelLongPress();
    }
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (pointerIdRef.current != null) {
      (e.currentTarget as HTMLElement).releasePointerCapture(pointerIdRef.current);
      pointerIdRef.current = null;
    }
    cancelLongPress();
  };

  const handlePointerCancel = (e: React.PointerEvent) => {
    if (pointerIdRef.current != null) {
      (e.currentTarget as HTMLElement).releasePointerCapture(pointerIdRef.current);
      pointerIdRef.current = null;
    }
    cancelLongPress();
  };

  useEffect(() => () => { removeScrollGuards(); cancelLongPress(); }, []);

  useEffect(() => {
    if (!showMobileFeedback) {
      setEnableClickOutside(false);
      setPressedButton(null);
      setDeselecting(null);

      if (longPressTimeoutRef.current) {
        clearTimeout(longPressTimeoutRef.current);
      }
    }
  }, [showMobileFeedback]);

  // Click outside handler
  useEffect(() => {
    if (showMobileFeedback && enableClickOutside) {
      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        const popup = document.querySelector('.mobileFeedbackPopup');
        if (popup && popup.contains(target)) {
          return;
        }
        setShowMobileFeedback(false);
      };

      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [showMobileFeedback, enableClickOutside]);

  const handleChecklistOpen = async (checklistId: string) => {
    setChecklistId(checklistId);
    setChecklistState(ChecklistState.OPEN);

    await getChecklist(checklistId);
  };

  const handleAlertsClick = () => {
    if (message.metadata && message.metadata?.alerts) {
      setIsAlertPopupOpen(true);
    }
  };

  const handleFeaturesClick = () => {
    if (message.metadata?.features) {
      setShowFeatureSelection(true);
    }
  };

  const hasMetadata = message.metadata && (
    (message.metadata.alerts && message.metadata.alerts.length > 0) ||
    (message.metadata.checklists && message.metadata.checklists.length > 0) ||
    (message.metadata.features && message.metadata.features.length > 0) ||
    (message.metadata.links && message.metadata.links.length > 0)
  );

  function stripSeconds(ts?: string) {
    if (!ts) return '';
    // If it looks like "HH:MM:SS", return "HH:MM"
    const m = ts.match(/^(\d{1,2}:\d{2})/);
    return m ? m[1] : ts; // fallback unchanged if unexpected format
  }

  const visualizer = useMemo(() => {
    if (isLatestMessage && (!isFirstMessage || isExhibitionMode) && !isMuted) {
      const wrapperClass = (audioOutputFileName || (isFirstMessage && overrideAudioUrl))
      ? `${styles.visualizerWrapper} ${styles.hasAudio}`
      : `${styles.visualizerWrapper} ${styles.noAudio}`;

      return (
        <div className={wrapperClass}>
          <PlaybackVisualizer
            audioOutputFileName={audioOutputFileName}
            isMuted={isMuted}
            overrideUrl={isFirstMessage ? overrideAudioUrl : undefined}
          />
        </div>
      );
    } else if (isLatestMessage && !isFirstMessage && isMuted) {
      return (<div className={styles.visualizerPlaceholder} ref={visualizerRef}></div>)
    }
    return null;
  }, [isLatestMessage, isFirstMessage, isMuted, audioOutputFileName, overrideAudioUrl, isExhibitionMode, onPlaybackFinish]);


  // Determine if this message is currently playing
  const isMessagePlaying = isLatestMessage && isPlaying && audioOutputFileName && !isMuted;

  return (
    <div className={styles.aiMessageContainer}>
        <>

          <div
            className={`${styles.messageWithFeedback} ${isLongPressing ? styles.longPressing : ''}`}
            ref={messageRef}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}

            onContextMenu={(e) => {
              if (isLongPressing || longPressTriggeredRef.current) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}

            // prevent the synthetic click that follows a long-press
            onClickCapture={(e) => {
              if (longPressTriggeredRef.current) {
                e.preventDefault();
                e.stopPropagation();
                longPressTriggeredRef.current = false; // reset after swallowing the click
              }
            }}
          >
            <div className={styles.aiMessageBubble}>

              {message.text && (
                <div id="aiMessage" className={`${styles.aiMessage} ${isMessagePlaying ? styles.isPlaying : ''}`}>
                  <MarkdownRenderer markdown={message.text} />

                  {hasMetadata && (
                    <MetadataBubble
                      alerts={message.metadata?.alerts || []}
                      checklists={message.metadata?.checklists}
                      features={message.metadata?.features}
                      links={message.metadata?.links}
                      onAlertClick={() => { handleAlertsClick(); }}
                      onChecklistClick={(checklistId) => { handleChecklistOpen(checklistId); }}
                      onFeaturesClick={() => { handleFeaturesClick(); }}
                      onLinkClick={(url) => { window.open(url, '_blank'); }}
                      margin={true}
                    />
                  )}
                </div>)}

              {!isExhibitionMode && message.text && (
                <FeedbackButtons
                  selectedFeedback={selectedFeedback}
                  onFeedback={handleFeedback}
                />
              )}

              {visualizer}
              {message.text && (
                <div className={styles.timestampRow}>
                  <div className={styles.timestamp}>
                    {isExhibitionMode ? stripSeconds(message.timestamp) : message.timestamp}
                  </div>
                  {(message.metadata?.context_relevant === false) &&
                    <div className={styles.warningIconContainer} tabIndex={0}>
                      <img src={warningIcon} className={styles.warningIcon} alt="warning" />
                      <div className={styles.warningTooltip}>{t("noRelevantContext")}</div>
                  </div>
                  }
                </div>
              )}
            </div>
          </div>

          {showMobileFeedback && (
            <MobileFeedbackPopup
              touchPosition={touchPosition}
              selectedFeedback={selectedFeedback}
              deselecting={deselecting}
              pressedButton={pressedButton}
              onFeedback={handleFeedback}
              onClose={() => setShowMobileFeedback(false)}
            />
          )}

          {showFeedback && (
            <FeedbackDialog
              isShowing={isShowingFeedback}
              isHiding={isHiding}
              containerRef={containerRef}
              dialogRef={dialogRef}
              selectedReasons={selectedReasons}
              otherReason={otherReason}
              showOtherInput={showOtherInput}
              onFeedbackReason={handleFeedbackReason}
              onOtherReasonChange={(value) => setOtherReason(value)}
              onShowOtherInput={handleShowOtherInput}
              onHideFeedback={handleHideFeedback}
            />
          )}

          {renderChecklist && (
            <ChecklistPanel
              onImageClick={openModal}
              onRetry={handleChecklistOpen}
            />
          )}

          {showFeatureSelection && message.metadata?.features && (
            <FeatureSelection
              features={message.metadata.features}
              onClose={() => setShowFeatureSelection(false)}
              onFeatureSelect={(selections) => {
                onFeatureSelect(selections);
                setShowFeatureSelection(false);
              }}
            />
          )}

          {isAlertPopupOpen && (
            <>
              <Backdrop
                isVisible={isAlertPopupOpen}
                onClick={() => setIsAlertPopupOpen(false)}
              />
              <AlertPopup
                alerts={message.metadata?.alerts || []}
                onClose={() => setIsAlertPopupOpen(false)}
              />
            </>
          )}
        </>
    </div>
  );
}

export default AiMessage;